"use client";

import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { sendErrorToTelegram } from "@/lib/telegram";
import { MetaMaskMobileBridgeError } from "@/lib/wallet/errors";
import {
  type ContractErrorResult,
  classifyApiError,
  classifyContractError,
  isUserActionError,
} from "./useContractError";

const telegramContext = () => ({
  url: typeof window !== "undefined" ? window.location.href : undefined,
  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  environment: process.env.NODE_ENV?.toUpperCase() || "UNKNOWN",
});

/**
 * Binds contract/API error classification to the active locale + a sonner toast.
 * Returns stable callbacks usable inside mutation `onError` handlers.
 */
export function useContractErrorHandler() {
  const t = useTranslations("errors.contract");

  const resolve = useCallback(
    (r: ContractErrorResult): string =>
      r.kind === "key" ? t(r.key) : r.message,
    [t],
  );

  const getMessage = useCallback(
    (error: unknown) => resolve(classifyContractError(error)),
    [resolve],
  );

  const handleContractError = useCallback(
    (error: unknown) => {
      toast.error(getMessage(error));
      if (
        !isUserActionError(error) &&
        !(error instanceof MetaMaskMobileBridgeError)
      ) {
        sendErrorToTelegram(
          error instanceof Error ? error : new Error(String(error)),
          telegramContext(),
        ).catch(() => {});
      }
    },
    [getMessage],
  );

  const handleApiError = useCallback(
    (error: unknown) => {
      toast.error(resolve(classifyApiError(error)));
      if (error instanceof Error)
        sendErrorToTelegram(error, telegramContext()).catch(() => {});
    },
    [resolve],
  );

  return useMemo(
    () => ({ handleContractError, handleApiError, getMessage }),
    [handleContractError, handleApiError, getMessage],
  );
}
